<template>
  <div>
    <InputButton
      :icon="buttonIcon"
      :fill="buttonFill"
      :label="buttonLabel"
      @click="toggleVisible"
    />
    <div v-if="visible" class="dropdown-picker card p-0">
      <div class="picker-search card-header">
        <input
          ref="searchInput"
          v-model="search"
          v-focus
          aria-label="Search"
          name="search"
          type="text"
          autocomplete="off"
          placeholder="Search..."
          @keydown.enter.prevent="insertSelected(selectedItem)"
        >
        <InputButton
          icon="times-circle"
          fill="far"
          label="Clear Search"
          :disabled="search.length === 0"
          @click="clearSearch"
        />
      </div>
      <div class="card-body d-flex flex-column h-100 py-0">
        <slot name="default" :items="filteredItems" :select="select" :insertSelected="insertSelected">
          <TabPreview v-if="hasItems" :tabs="Object.values(filteredItems).flatMap(category => Object.entries(category).map(([key, value]) => ({ [key]: value })))" :quick-submit="(value) => insertSelected(value)" @change="select">
            <template #default="{activeText, postSubmit}">
              <div class="pt-3">
                "{{ activeText }}"
              </div>
              <br>
              <button class="btn btn-primary" @click="insertSelected(activeText) && postSubmit()">
                {{ buttonLabel }}
              </button>
            </template>
          </TabPreview>
          <div v-else class="text-center mt-3">
            No matching items.
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import InputButton from "@/FormWidgets/WidgetInputArea/components/InputButton.vue";
import TabPreview from "@/FormWidgets/WidgetInputArea/components/TabPreview.vue";

export default {
  name: "InputPicker",
  // Vue 3:
  // emits: ['input', 'cancelTrigger'],
  components: {TabPreview, InputButton},
  directives: {
    focus: {
      inserted(el) {
        el.focus()
      },
    },
  },
  props: {
    buttonIcon: {
      type: String,
      required: true,
    },
    buttonFill: {
      type: String,
      required: false,
    },
    buttonLabel: {
      type: String,
      required: true,
    },
    textarea: {
      type: Function,
      required: true,
    },
    trigger: {
      type: [KeyboardEvent, Boolean],
      default: false,
    },
    triggerStart: {
      type: String, // Regex
      default: "\\.",
    },
    triggerLastCharacter: {
      type: String, // Regex
      default: "[A-Za-z0-9]",
    },
    // If true, only match if key starts with search term
    // Else, match liberally
    anchorSearchToKeyStart: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      search: '',
      visible: false,
      selectedItem: null,
      filteredItems: this.items,
    }
  },
  computed: {
    hasItems(){
      return Object.keys(this.filteredItems).length > 0;
    }
  },
  watch:{
    search: function(value) {
      this.filterItems(value);
      if(Object.keys(this.filteredItems).length === 0) {
        this.select(null);
      }
    },
    trigger: function (event) {
      // Return if there is no event (e.g., trigger was canceled)
      if(event === false){
        return;
      }
      if (event.key.length === 1 && event.key.match(new RegExp(this.triggerLastCharacter)) && this.triggerOffset() > 0) {
        this.visible = true;
        this.search = event.key;
      } else{
        this.$emit('cancelTrigger', false);
      }
    },
  },
  mounted() {
    document.addEventListener('keyup', this.escape);
    window.addEventListener('click', this.close);
  },
  destroyed() {
    document.removeEventListener('keyup', this.escape);
    window.removeEventListener('click', this.close);
  },
  methods: {
    clearSearch(){
      this.search = '';
      this.$refs.searchInput.focus();
    },
    select(item) {
      this.selectedItem = item;
    },
    toggleVisible() {
      this.visible = !this.visible;
      this.search = '';
      this.$emit('cancelTrigger', false);
    },
    hide(refocus) {
      this.visible = false;
      this.$emit('cancelTrigger', refocus);
    },
    escape(e) {
      if (this.visible === true && e.keyCode === 27) {
        this.hide(true);
      }
    },
    close(e) {
      if (! this.$el.contains(e.target)) {
        this.hide(false);
      }
    },
    filterItems() {
      if (this.search.length === 0) {
        this.filteredItems = this.items;
        return;
      }
      const filtered = {};
      let firstMatch = null;
      for (const category in this.items) {
        const matchedItems = {};
        for (const itemName in this.items[category]) {
          if(this.anchorSearchToKeyStart && itemName.toLowerCase().startsWith(this.search.toLowerCase())) {
          // ^ Match only at start of key
            matchedItems[itemName] = this.items[category][itemName];
            if(firstMatch === null) {
              firstMatch = this.items[category][itemName];
            }
          } else if (!this.anchorSearchToKeyStart && (itemName.toLowerCase().includes(this.search.toLowerCase()) ||
              this.items[category][itemName].toLowerCase().includes(this.search.toLowerCase()))) {
            // ^ Match liberally
            matchedItems[itemName] = this.items[category][itemName];
            if(firstMatch === null) {
              firstMatch = this.items[category][itemName];
            }
          }
          if (Object.keys(matchedItems).length > 0) {
            filtered[category] = matchedItems;
          }
        }
      }
      this.filteredItems = filtered;
    },
    triggerOffset() {
      const startPos = this.textarea().selectionStart;
      const found = this.textarea().value.substring(0, startPos).match(new RegExp(this.triggerStart + this.triggerLastCharacter + '$'));
      return (this.trigger && found) ? found[0].length : 0;
    },
    insertSelected() {
      const selected = this.selectedItem;
      if(!selected) {
        return;
      }
      const startPos = this.textarea().selectionStart;
      const offset = this.triggerOffset();
      const endPos = this.textarea().selectionEnd;
      const newValue = this.textarea().value.substring(0, startPos - offset)
          + selected
          + this.textarea().value.substring(endPos, this.textarea().value.length);
      this.$emit('input', newValue);

      this.hide(true);

      this.$nextTick(() => {
        this.textarea().selectionStart = this.textarea().selectionEnd = startPos + selected.length - offset
      });
    }
  }
}
</script>

<style scoped>
/* See https://codepen.io/DCzajkowski/pen/jzLzWp */
.dropdown-picker {
  position: absolute;
  z-index: 1;
  border: 1px solid #ccc;
  /* 1000px is roughly the size of the input,
     75vm looks best at most widths
     25rem is needed for mobile */
  width: min(1000px, max(70vw, 25rem));
  right: 0;
  top: -21rem;
  height: 20rem;
  overflow: scroll;
  padding: 1rem;
  box-sizing: border-box;
  border-radius: 0.5rem;
  background: #fff;
  box-shadow: 1px 1px 8px #c7dbe6;
}

.picker-search {
  display: flex;
}

.picker-search > input {
  flex: 1;
  border-radius: 10rem;
  border: 1px solid #ccc;
  padding: 0.5rem 1rem;
  outline: none;
}

.picker-search button {
  position:absolute;
  right: 3rem;
  top: 2.3rem;
}

/* If it's hidden with Vue instead, it's treated as outside the dropdown in the onClick listener and closes the dropdown */
.picker-search button:disabled {
  visibility: hidden;
}

</style>
